import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Loader2, Bell, BellOff, CheckCircle2,
  AlertTriangle, Clock, Filter, RefreshCw, Megaphone
} from 'lucide-react';
import api from '../../utils/api';


const severityConfig = {
  critical: { color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, iconColor: 'text-red-500', dot: 'bg-red-500' },
  warning: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-500', dot: 'bg-amber-500' },
  tip: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Bell, iconColor: 'text-blue-500', dot: 'bg-blue-500' },
};

const AlertCard = ({ alert, onResolve, onMarkRead, resolving, t, timeAgo }) => {
  const config = severityConfig[alert.severity] || severityConfig.critical;
  const Icon = config.icon;
  const isUnread = !alert.read;
  const isResolved = alert.resolved;
  const ts = new Date(alert.created_at);
  const timeAgoStr = timeAgo(ts);

  return (
    <Card
      className={`border transition-all ${isUnread ? 'border-l-4 border-l-red-500 bg-white shadow-sm' : 'border-slate-200 bg-slate-50/50'} ${isResolved ? 'opacity-60' : ''}`}
      data-testid={`alert-card-${alert.alert_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${isResolved ? 'bg-slate-100' : config.color}`}>
            {isResolved
              ? <CheckCircle2 className="h-4 w-4 text-slate-400" />
              : <Icon className={`h-4 w-4 ${config.iconColor}`} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-semibold text-[#122033] truncate">{alert.campaign_title}</p>
              {isUnread && <span className={`h-2 w-2 rounded-full ${config.dot} shrink-0`} />}
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${config.color}`} data-testid={`alert-severity-${alert.alert_id}`}>
                {alert.severity}
              </Badge>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">{alert.message}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-[10px] text-slate-400 flex items-center gap-1">
                <Clock className="h-3 w-3" /> {timeAgoStr}
              </span>
              {isResolved && alert.resolved_at && (
                <span className="text-[10px] text-emerald-500 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> {t('provider.notificationsCenter.resolved', { time: timeAgo(new Date(alert.resolved_at)) })}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            {!isResolved && (
              <Button
                size="sm"
                variant="outline"
                className="text-[10px] h-7 px-2"
                onClick={() => onResolve(alert.alert_id)}
                disabled={resolving}
                data-testid={`resolve-alert-${alert.alert_id}`}
              >
                {resolving ? <Loader2 className="h-3 w-3 animate-spin" /> : t('provider.common.resolve')}
              </Button>
            )}
            {isUnread && !isResolved && (
              <Button
                size="sm"
                variant="ghost"
                className="text-[10px] h-7 px-2 text-slate-400"
                onClick={() => onMarkRead(alert.alert_id)}
                data-testid={`read-alert-${alert.alert_id}`}
              >
                {t('provider.common.markRead')}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

function getTimeAgo(date, t) {
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return t('provider.common.justNow');
  if (diff < 3600) return t('provider.common.minsAgo', { n: Math.floor(diff / 60) });
  if (diff < 86400) return t('provider.common.hrsAgo', { n: Math.floor(diff / 3600) });
  return t('provider.common.daysAgo', { n: Math.floor(diff / 86400) });
}

export default function ClinicNotificationsCenter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timeAgo = (d) => getTimeAgo(d, t);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active'); // active | all
  const [resolvingId, setResolvingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({ count: 0, unread_count: 0 });

  const token = localStorage.getItem('clinic_admin_token') || '';

  const fetchAlerts = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const includeResolved = filter === 'all';
      const res = await api.get(`/clinic/campaigns/alerts?include_resolved=${includeResolved}`, {
      });
      const data = res.data;
      setAlerts(data.alerts || []);
      setStats({ count: data.count || 0, unread_count: data.unread_count || 0 });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, filter]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleResolve = async (alertId) => {
    setResolvingId(alertId);
    try {
      const res = await api.post(`/clinic/campaigns/alerts/${alertId}/resolve`, {
        method: 'POST',
      });
      await fetchAlerts();
    } finally {
      setResolvingId(null);
    }
  };

  const handleMarkRead = async (alertId) => {
    try {
      await api.post(`/clinic/campaigns/alerts/${alertId}/read`, {
        method: 'POST',
      });
      await fetchAlerts();
    } catch { /* silent */ }
  };

  const campaignAlerts = alerts.filter(a => a.alert_type === 'campaign_alert_triggered');
  const otherAlerts = alerts.filter(a => a.alert_type !== 'campaign_alert_triggered');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="notifications-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BA4C5]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="clinic-notifications-center">
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/clinic/dashboard')}
            className="h-8 w-8 p-0"
            data-testid="notifications-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-[#122033]">{t('provider.notificationsCenter.title')}</h1>
            <p className="text-xs text-slate-400">{t('provider.notificationsCenter.subtitle')}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchAlerts(true)}
            disabled={refreshing}
            className="h-8 gap-1 text-xs"
            data-testid="notifications-refresh-btn"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            {t('provider.common.refresh')}
          </Button>
        </div>

        {/* Stats Bar */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2">
            <Bell className="h-4 w-4 text-[#3BA4C5]" />
            <span className="text-sm font-semibold text-[#122033]" data-testid="total-alerts-count">{stats.count}</span>
            <span className="text-xs text-slate-400">{t('provider.common.total')}</span>
          </div>
          {stats.unread_count > 0 && (
            <div className="flex items-center gap-2 bg-red-50 rounded-lg border border-red-200 px-3 py-2">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              <span className="text-sm font-semibold text-red-700" data-testid="unread-alerts-count">{stats.unread_count}</span>
              <span className="text-xs text-red-400">{t('provider.common.unread')}</span>
            </div>
          )}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <button
            className={`text-xs px-2.5 py-1 rounded-full transition-colors ${filter === 'active' ? 'bg-[#122033] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            onClick={() => setFilter('active')}
            data-testid="filter-active"
          >
            {t('provider.notificationsCenter.filterActive')}
          </button>
          <button
            className={`text-xs px-2.5 py-1 rounded-full transition-colors ${filter === 'all' ? 'bg-[#122033] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            onClick={() => setFilter('all')}
            data-testid="filter-all"
          >
            {t('provider.notificationsCenter.filterAll')}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-xs text-red-600" data-testid="notifications-error">
            {error}
          </div>
        )}

        {/* Campaign Alerts Section */}
        {campaignAlerts.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Megaphone className="h-4 w-4 text-red-500" />
              <h2 className="text-sm font-semibold text-[#122033]">{t('provider.notificationsCenter.campaignAlerts')}</h2>
              <Badge variant="outline" className="text-[10px]">{campaignAlerts.length}</Badge>
            </div>
            <div className="space-y-2" data-testid="campaign-alerts-list">
              {campaignAlerts.map(alert => (
                <AlertCard
                  key={alert.alert_id}
                  alert={alert}
                  onResolve={handleResolve}
                  onMarkRead={handleMarkRead}
                  resolving={resolvingId === alert.alert_id}
                  t={t}
                  timeAgo={timeAgo}
                />
              ))}
            </div>
          </div>
        )}

        {/* Other Alerts */}
        {otherAlerts.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="h-4 w-4 text-slate-500" />
              <h2 className="text-sm font-semibold text-[#122033]">{t('provider.notificationsCenter.systemNotifications')}</h2>
              <Badge variant="outline" className="text-[10px]">{otherAlerts.length}</Badge>
            </div>
            <div className="space-y-2" data-testid="system-alerts-list">
              {otherAlerts.map(alert => (
                <AlertCard
                  key={alert.alert_id}
                  alert={alert}
                  onResolve={handleResolve}
                  onMarkRead={handleMarkRead}
                  resolving={resolvingId === alert.alert_id}
                  t={t}
                  timeAgo={timeAgo}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {alerts.length === 0 && (
          <Card className="border border-slate-200" data-testid="no-alerts-empty">
            <CardContent className="py-12 text-center">
              <BellOff className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-500">{t('provider.notificationsCenter.noAlerts')}</p>
              <p className="text-xs text-slate-400 mt-1">
                {filter === 'active'
                  ? t('provider.notificationsCenter.allResolved')
                  : t('provider.notificationsCenter.noAlertsYet')}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
